import { FormProvider, useForm } from 'react-hook-form';
import { Alert, Button, Card, Heading, Paragraph, Textfield } from '@digdir/designsystemet-react';
import { ChevronRightIcon } from '@navikt/aksel-icons';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { SolcellerFormData } from '../types';
import { useSubmitSolceller } from '../useSubmitSolceller';
import { GeoLocationInput, useGeoLocation } from '../hooks/useGeoLocation';
import { hasValidCoordinates } from '../utils/coordinates';
import {
  getCoordinateDistanceWarningMessage,
  type SolarCoordinateValidationContext,
  validateSolarPanelCoordinates
} from '../utils/geoValidation';
import { FormErrors } from '../../../../../components/form/formErrors';
import { useRegistrationSteps } from '../../../hooks/useRegistrationSteps';
import { DetailingStep } from '../../../../../components/registering/utils/registerEnums';
import { FormBackButton } from '../../../components/formBackButton';
import { ErrorBox } from '../../../../../components/errorBox';
import { RootState } from '../../../../../store/rootReducer';

import { ProductionData } from './components/productionData';
import { KwpPanels } from './components/kwpPanels';
import { ProductionCapacityRadio } from './components/productionCapacityRadio';
import { ButtonWrapper, CardContentWrapper, MainContentWrapper } from './components/styledComponents';
import { KwpInfoPopOver } from './components/kwpInfoPopOver';
import { LocationMap } from './components/locationMap';
import { SqmPanels } from './components/sqmPanels';
import { SolarSharing } from './components/solarSharing';

interface Props {
  defaultValues?: SolcellerFormData;
  geoLocationInput?: GeoLocationInput;
  registrationId?: string;
}

export const Form: FC<Props> = ({ defaultValues, geoLocationInput, registrationId }) => {
  const { t } = useTranslation();
  const { submitSolceller, isLoading, submitError } = useSubmitSolceller();
  const navigate = useNavigate();
  const { previousStep } = useRegistrationSteps(DetailingStep.Solceller);
  const {
    latitude: fetchedLatitude,
    longitude: fetchedLongitude,
    status: geoLocationStatus,
    hitCount: geoLocationHitCount
  } = useGeoLocation(geoLocationInput);
  const byggAr = useSelector((state: RootState) => state.register.userInput?.bygningsdetaljer?.byggAr);

  const methods = useForm<SolcellerFormData>({
    // Match other V2025 step forms (e.g. ventilasjon): do not use root shouldUnregister, so values
    // stay in form state when conditional fields unmount (progressive disclosure).
    defaultValues: {
      ...defaultValues,
      kwpPanels: defaultValues?.kwpPanels || [{}],
      sqmPanels: defaultValues?.sqmPanels || [{}]
    }
  });

  const { getValues, trigger, setValue } = methods;

  const validateSolarInstallationYear = (value?: number): string | true => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return t('register.solar.form.installationYear.missing');
    }
    const str = value.toString();
    if (!/^(19|20)\d{2}$/.test(str)) {
      return t('register.solar.form.installationYear.invalid');
    }
    const currentYear = new Date().getFullYear();
    if (value > currentYear) {
      return t('register.solar.form.installationYear.futureNotAllowed');
    }
    if (byggAr && value < byggAr) {
      return t('register.solar.form.installationYear.tooEarly', { byggAr });
    }
    return true;
  };

  const coordinateValidationContext = useMemo(
    (): SolarCoordinateValidationContext => ({
      referenceLatitude: fetchedLatitude,
      referenceLongitude: fetchedLongitude,
      hasReferencePoint: hasValidCoordinates(fetchedLatitude, fetchedLongitude),
      referenceIsKommuneCenter: geoLocationStatus === 'not-found'
    }),
    [fetchedLatitude, fetchedLongitude, geoLocationStatus]
  );

  const validateCoordinatesPair = useCallback((): true | string => {
    return validateSolarPanelCoordinates(getValues('latitude'), getValues('longitude'), t);
  }, [getValues, t]);

  const capacitySelection = methods.watch('hasCapacityData');
  const latitude = methods.watch('latitude');
  const longitude = methods.watch('longitude');
  const errors = methods.formState.errors;

  const coordinateDistanceWarning = useMemo(
    () => getCoordinateDistanceWarningMessage(latitude, longitude, coordinateValidationContext, t),
    [latitude, longitude, coordinateValidationContext, t]
  );

  const navigateBack = () => navigate(`../${previousStep}`);

  useEffect(() => {
    const hasExistingCoordinates = hasValidCoordinates(latitude, longitude);
    if (hasExistingCoordinates) {
      return;
    }

    const hasFetchedCoordinates = hasValidCoordinates(fetchedLatitude, fetchedLongitude);
    if (!hasFetchedCoordinates) {
      return;
    }

    setValue('latitude', fetchedLatitude, { shouldDirty: false, shouldValidate: false });
    setValue('longitude', fetchedLongitude, { shouldDirty: false, shouldValidate: false });
  }, [fetchedLatitude, fetchedLongitude, latitude, longitude, setValue]);

  useEffect(() => {
    if (!hasValidCoordinates(latitude, longitude)) {
      return;
    }
    void trigger('latitude');
  }, [latitude, longitude, trigger]);

  const onCoordinateChange = (coordinates: { latitude: number; longitude: number }) => {
    setValue('latitude', coordinates.latitude, { shouldDirty: true, shouldValidate: true });
    setValue('longitude', coordinates.longitude, { shouldDirty: true, shouldValidate: true });
  };

  const onSubmit = (data: SolcellerFormData) => submitSolceller(data);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        <Paragraph>{t('register.solar.form.samePropertyGuidance')}</Paragraph>
        <MainContentWrapper>
          <Card>
            <Heading data-size="sm" level={2}>
              {t('register.solar.form.sharing.header')}
            </Heading>
            <CardContentWrapper>
              <SolarSharing />
            </CardContentWrapper>
          </Card>

          <Card>
            <Heading data-size="sm" level={2}>
              {t('register.solar.form.header.solarPanelInfo')}
            </Heading>
            <CardContentWrapper>
              <Textfield
                type="number"
                maxLength={4}
                label={t('register.solar.form.installationYear.label')}
                error={errors.installationYear?.message}
                {...methods.register('installationYear', {
                  valueAsNumber: true,
                  validate: validateSolarInstallationYear
                })}
              />
              <ProductionData />
            </CardContentWrapper>
          </Card>

          <Card>
            <Heading data-size="sm" level={2}>
              {t('register.solar.form.productionCapacity.header')}
            </Heading>
            <Paragraph>{t('register.solar.form.productionCapacity.description')}</Paragraph>
            <KwpInfoPopOver />

            <CardContentWrapper>
              <ProductionCapacityRadio />
              {/* Panel inputs can be either in kWp or in kvm (m²) */}
              {capacitySelection === 'yes' && <KwpPanels />}
              {capacitySelection === 'no' && <SqmPanels />}
            </CardContentWrapper>
          </Card>
          <Card>
            <Heading data-size="sm" level={2}>
              {t('register.solar.form.location.header')}
            </Heading>
            <Paragraph>{t('register.solar.form.location.description')}</Paragraph>
            <CardContentWrapper>
              <input type="hidden" {...methods.register('latitude', { validate: validateCoordinatesPair })} />
              <input type="hidden" {...methods.register('longitude')} />
              <div id="solceller-location-map-anchor" tabIndex={-1} style={{ scrollMarginTop: 'var(--ds-size-11)' }}>
                <LocationMap
                  latitude={latitude}
                  longitude={longitude}
                  status={geoLocationStatus}
                  hitCount={geoLocationHitCount}
                  onCoordinateChange={onCoordinateChange}
                />
              </div>
              {coordinateDistanceWarning && (
                <Alert data-color="warning" style={{ marginTop: 'var(--ds-size-4)' }}>
                  <Paragraph>{coordinateDistanceWarning}</Paragraph>
                </Alert>
              )}
            </CardContentWrapper>
          </Card>
          <FormErrors
            errors={errors}
            fieldAnchorIds={{ latitude: 'solceller-location-map-anchor' }}
          />
          <ErrorBox error={submitError} registrationId={registrationId} />
          <ButtonWrapper>
            <FormBackButton<SolcellerFormData>
              isLoading={isLoading}
              onSuccess={navigateBack}
              onSubmit={({ data, onSuccess }) => submitSolceller(data, onSuccess, false)}
            />
            <Button loading={isLoading} type="submit">
              {t('register.solar.form.buttons.continue')} <ChevronRightIcon />
            </Button>
          </ButtonWrapper>
        </MainContentWrapper>
      </form>
    </FormProvider>
  );
};
